import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * TableRenderingManager Context
 * 
 * Centralized state management for table rendering with performance optimizations.
 * Features:
 * - Single source of truth for all table state
 * - Batched updates using requestAnimationFrame
 * - Deduplication of updates by entity key
 * - Memoized visibility computations
 * - Immutable update patterns for React optimization
 */

// Types
interface FilterState {
  search: string;
  clientId: string;
  leadArtistId: string;
  sequencesPerProject: Record<string, number[]>; // projectId -> sequence numbers
}

interface TableState {
  // Expanded state tracking
  expandedKeys: Set<string>;
  
  // Filter state
  filters: FilterState;
  
  // Entity maps for quick lookups
  itemMaps: {
    pulses: Map<number, any>;
    tasks: Map<number, any>;
    sequences: Map<number, any>;
  };
  
  // Visibility cache for computed states
  visibilityCache: Map<string, boolean>;
}

type UpdateType = 'expand' | 'collapse' | 'filter' | 'entity' | 'batch';

interface UpdateOperation {
  type: UpdateType;
  entityKey: string;
  entityType?: 'pulse' | 'task' | 'sequence' | 'project' | 'year';
  payload: any;
  timestamp: number;
}

interface TableRenderingContextValue {
  // State access
  state: TableState;
  
  // Core methods
  applyUpdates: (updates: UpdateOperation[]) => void;
  setExpanded: (key: string, expanded: boolean) => void;
  toggleExpanded: (key: string) => void;
  expandMultiple: (keys: string[]) => void;
  collapseAll: () => void;
  setFilters: (filters: Partial<FilterState>) => void;
  getVisibleNodes: (projectId: string) => string[];
  
  // Batch update methods
  queueUpdate: (update: UpdateOperation) => void;
  flushUpdates: () => void;
  
  // Entity management
  updateEntity: (type: 'pulse' | 'task' | 'sequence', id: number, data: any) => void;
  getEntity: (type: 'pulse' | 'task' | 'sequence', id: number) => any;
  
  // Performance helpers
  isExpanded: (key: string) => boolean;
  isVisible: (key: string) => boolean;
  clearVisibilityCache: () => void;
}

const TableRenderingContext = createContext<TableRenderingContextValue | undefined>(undefined);

export function TableRenderingProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  
  // Main state
  const [state, setState] = useState<TableState>({
    expandedKeys: new Set(),
    filters: {
      search: '',
      clientId: '',
      leadArtistId: '',
      sequencesPerProject: {}
    },
    itemMaps: {
      pulses: new Map(),
      tasks: new Map(),
      sequences: new Map()
    },
    visibilityCache: new Map()
  });
  
  // Update queue for batching
  const updateQueueRef = useRef<Map<string, UpdateOperation>>(new Map());
  const flushTimeoutRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const subscribersRef = useRef<Set<() => void>>(new Set());
  
  /**
   * Queue an update for batched processing
   * Deduplicates by entity key to prevent redundant updates
   */
  const queueUpdate = useCallback((update: UpdateOperation) => {
    // Add timestamp if not present
    if (!update.timestamp) {
      update.timestamp = performance.now();
    }
    
    // Deduplicate by entity key - later updates override earlier ones
    updateQueueRef.current.set(update.entityKey, update);
    
    // Schedule flush if not already scheduled
    if (!flushTimeoutRef.current && !rafIdRef.current) {
      // Use microtask for immediate flush, or RAF for batching
      if (update.type === 'filter' || update.type === 'batch') {
        // Filters and batch operations should apply immediately
        queueMicrotask(() => flushUpdates());
      } else {
        // Visual updates can be batched with RAF
        rafIdRef.current = requestAnimationFrame(() => {
          flushUpdates();
        });
      }
    }
  }, []);
  
  /**
   * Flush all queued updates in a single batch
   * This minimizes React re-renders and improves performance
   */
  const flushUpdates = useCallback(() => {
    if (updateQueueRef.current.size === 0) {
      return;
    }
    
    const updates = Array.from(updateQueueRef.current.values());
    updateQueueRef.current.clear();
    
    // Cancel any pending flush
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    
    // Apply all updates in a single state update
    setState(prevState => {
      let newState = { ...prevState };
      let visibilityCacheCleared = false;
      
      for (const update of updates) {
        switch (update.type) {
          case 'expand':
            newState.expandedKeys = new Set(newState.expandedKeys);
            newState.expandedKeys.add(update.entityKey);
            if (!visibilityCacheCleared) {
              newState.visibilityCache = new Map();
              visibilityCacheCleared = true;
            }
            break;
            
          case 'collapse':
            newState.expandedKeys = new Set(newState.expandedKeys);
            newState.expandedKeys.delete(update.entityKey);
            if (!visibilityCacheCleared) {
              newState.visibilityCache = new Map();
              visibilityCacheCleared = true;
            }
            break;
            
          case 'filter':
            newState.filters = {
              ...newState.filters,
              ...update.payload
            };
            if (!visibilityCacheCleared) {
              newState.visibilityCache = new Map();
              visibilityCacheCleared = true;
            }
            break;
            
          case 'entity':
            if (update.entityType) {
              const entityMap = new Map(newState.itemMaps[`${update.entityType}s` as keyof typeof newState.itemMaps]);
              entityMap.set(parseInt(update.entityKey), update.payload);
              newState.itemMaps = {
                ...newState.itemMaps,
                [`${update.entityType}s`]: entityMap
              };
            }
            break;
            
          case 'batch':
            // Handle batch operations
            const { expandedKeys, filters, entities } = update.payload;
            if (expandedKeys) {
              newState.expandedKeys = new Set(expandedKeys);
            }
            if (filters) {
              newState.filters = { ...newState.filters, ...filters };
            }
            if (entities) {
              // Update entity maps
              newState.itemMaps = { ...newState.itemMaps };
              for (const [type, items] of Object.entries(entities)) {
                const mapKey = `${type}s` as keyof typeof newState.itemMaps;
                if (mapKey in newState.itemMaps) {
                  const entityMap = new Map(newState.itemMaps[mapKey]);
                  for (const [id, data] of Object.entries(items as Record<string, any>)) {
                    entityMap.set(parseInt(id), data);
                  }
                  newState.itemMaps[mapKey] = entityMap;
                }
              }
            }
            if (!visibilityCacheCleared) {
              newState.visibilityCache = new Map();
              visibilityCacheCleared = true;
            }
            break;
        }
      }
      
      return newState;
    });
    
    // Notify subscribers after batch completes
    subscribersRef.current.forEach(callback => callback());
  }, []);
  
  /**
   * Apply multiple updates at once
   */
  const applyUpdates = useCallback((updates: UpdateOperation[]) => {
    updates.forEach(update => queueUpdate(update));
  }, [queueUpdate]);
  
  /**
   * Set expanded state for a key
   */
  const setExpanded = useCallback((key: string, expanded: boolean) => {
    queueUpdate({
      type: expanded ? 'expand' : 'collapse',
      entityKey: key,
      payload: expanded,
      timestamp: performance.now()
    });
  }, [queueUpdate]);
  
  /**
   * Toggle expanded state
   */
  const toggleExpanded = useCallback((key: string) => {
    const isCurrentlyExpanded = state.expandedKeys.has(key);
    setExpanded(key, !isCurrentlyExpanded);
  }, [state.expandedKeys, setExpanded]);
  
  /**
   * Expand multiple keys at once
   */
  const expandMultiple = useCallback((keys: string[]) => {
    const updates = keys.map(key => ({
      type: 'expand' as UpdateType,
      entityKey: key,
      payload: true,
      timestamp: performance.now()
    }));
    applyUpdates(updates);
  }, [applyUpdates]);
  
  /**
   * Collapse all expanded items
   */
  const collapseAll = useCallback(() => {
    queueUpdate({
      type: 'batch',
      entityKey: 'collapse-all',
      payload: { expandedKeys: [] },
      timestamp: performance.now()
    });
  }, [queueUpdate]);
  
  /**
   * Update filters
   */
  const setFilters = useCallback((filters: Partial<FilterState>) => {
    queueUpdate({
      type: 'filter',
      entityKey: 'filters',
      payload: filters,
      timestamp: performance.now()
    });
  }, [queueUpdate]);
  
  /**
   * Update an entity in the cache
   */
  const updateEntity = useCallback((type: 'pulse' | 'task' | 'sequence', id: number, data: any) => {
    queueUpdate({
      type: 'entity',
      entityKey: id.toString(),
      entityType: type,
      payload: data,
      timestamp: performance.now()
    });
  }, [queueUpdate]);
  
  /**
   * Get an entity from the cache
   */
  const getEntity = useCallback((type: 'pulse' | 'task' | 'sequence', id: number) => {
    return state.itemMaps[`${type}s` as keyof typeof state.itemMaps].get(id);
  }, [state.itemMaps]);
  
  /**
   * Check if a key is expanded
   */
  const isExpanded = useCallback((key: string) => {
    return state.expandedKeys.has(key);
  }, [state.expandedKeys]);
  
  /**
   * Check if a node is visible based on filters and parent expansion
   * This is memoized in the visibility cache for performance
   */
  const isVisible = useCallback((key: string) => {
    // Check cache first
    if (state.visibilityCache.has(key)) {
      return state.visibilityCache.get(key)!;
    }
    
    // Compute visibility based on filters and parent expansion
    // This is a simplified implementation - extend based on actual requirements
    let visible = true;
    
    // Check filters
    if (state.filters.search) {
      // Implement search logic based on entity type
      visible = key.toLowerCase().includes(state.filters.search.toLowerCase());
    }
    
    // Cache the result
    state.visibilityCache.set(key, visible);
    return visible;
  }, [state.filters, state.visibilityCache]);
  
  /**
   * Get visible nodes for a project
   * This computes which items should be rendered based on expansion and filters
   */
  const getVisibleNodes = useCallback((projectId: string): string[] => {
    const visibleNodes: string[] = [];
    
    // Check if project is expanded
    if (!isExpanded(`project-${projectId}`)) {
      return [];
    }
    
    // Get pulses for this project from query cache
    const pulsesQuery = queryClient.getQueryData(['/api/projects', projectId, 'pulses']) as any;
    const pulses = pulsesQuery?.data || [];
    
    // Filter pulses based on current filters
    pulses.forEach((pulse: any) => {
      const pulseKey = `pulse-${pulse.id}`;
      
      // Apply filters
      let passesFilter = true;
      
      // Search filter
      if (state.filters.search) {
        passesFilter = pulse.pulseDescription?.toLowerCase().includes(state.filters.search.toLowerCase()) || false;
      }
      
      // Sequence filter for this project
      const projectSequences = state.filters.sequencesPerProject[projectId];
      if (projectSequences && projectSequences.length > 0) {
        // Check if pulse has any of the selected sequences
        const pulseSequences = pulse.sequences || [];
        passesFilter = passesFilter && pulseSequences.some((seq: any) => 
          projectSequences.includes(seq.sequenceNumber)
        );
      }
      
      if (passesFilter) {
        visibleNodes.push(pulseKey);
        
        // If pulse is expanded, include its tasks
        if (isExpanded(pulseKey)) {
          const tasks = pulse.tasks || [];
          tasks.forEach((task: any) => {
            visibleNodes.push(`task-${task.id}`);
          });
        }
      }
    });
    
    return visibleNodes;
  }, [state.filters, state.expandedKeys, isExpanded, queryClient]);
  
  /**
   * Clear visibility cache
   */
  const clearVisibilityCache = useCallback(() => {
    setState(prev => ({
      ...prev,
      visibilityCache: new Map()
    }));
  }, []);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, []);
  
  const value = useMemo(() => ({
    state,
    applyUpdates,
    setExpanded,
    toggleExpanded,
    expandMultiple,
    collapseAll,
    setFilters,
    getVisibleNodes,
    queueUpdate,
    flushUpdates,
    updateEntity,
    getEntity,
    isExpanded,
    isVisible,
    clearVisibilityCache
  }), [
    state,
    applyUpdates,
    setExpanded,
    toggleExpanded,
    expandMultiple,
    collapseAll,
    setFilters,
    getVisibleNodes,
    queueUpdate,
    flushUpdates,
    updateEntity,
    getEntity,
    isExpanded,
    isVisible,
    clearVisibilityCache
  ]);
  
  return (
    <TableRenderingContext.Provider value={value}>
      {children}
    </TableRenderingContext.Provider>
  );
}

/**
 * Hook to access the full table state
 */
export function useTableState() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useTableState must be used within TableRenderingProvider');
  }
  return context.state;
}

/**
 * Hook to get visible items for a specific project
 */
export function useVisibleItems(projectId: string) {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useVisibleItems must be used within TableRenderingProvider');
  }
  
  return useMemo(() => {
    return context.getVisibleNodes(projectId);
  }, [context, projectId]);
}

/**
 * Hook to submit batched updates
 */
export function useBatchedUpdate() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useBatchedUpdate must be used within TableRenderingProvider');
  }
  
  return {
    queueUpdate: context.queueUpdate,
    flushUpdates: context.flushUpdates
  };
}

/**
 * Hook to manage expanded state
 */
export function useTableExpansion() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useTableExpansion must be used within TableRenderingProvider');
  }
  
  return {
    expandedKeys: context.state.expandedKeys,
    isExpanded: context.isExpanded,
    setExpanded: context.setExpanded,
    toggleExpanded: context.toggleExpanded,
    expandMultiple: context.expandMultiple,
    collapseAll: context.collapseAll
  };
}

/**
 * Hook to manage filters
 */
export function useTableFilters() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useTableFilters must be used within TableRenderingProvider');
  }
  
  return {
    filters: context.state.filters,
    setFilters: context.setFilters
  };
}

/**
 * Hook to manage entity cache
 */
export function useTableEntityCache() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useTableEntityCache must be used within TableRenderingProvider');
  }
  
  return {
    updateEntity: context.updateEntity,
    getEntity: context.getEntity,
    itemMaps: context.state.itemMaps
  };
}

/**
 * Full context hook for advanced use cases
 */
export function useTableRendering() {
  const context = useContext(TableRenderingContext);
  if (!context) {
    throw new Error('useTableRendering must be used within TableRenderingProvider');
  }
  return context;
}